/**
 * The Ownership card on a hierarchy detail screen (f4.2, generalised by f5.1).
 *
 * f4.2 built this for Business Unit Groups and said the seam existed *"precisely
 * so this is a reuse"*. f5.1 takes it up: the component moved out of
 * `routes/business-unit-groups/` and gained `nodeType`, `nodeId`, `nodeName`
 * and `roleCodes` props. It is the SAME component on both screens -- `DEM` and
 * `CEM` on a group, `CM` on a Business Unit -- and f6.1 and f7.1 pass their own
 * node type without editing this file.
 *
 * Built from the prototype's own markup in `client-detail.html`: `card` /
 * `card-head` / `card-body stack-sm`, one `row` per owner with an `avatar sm`
 * of the holder's initials, a `user-name` and a `hint` reading
 * `<ROLE> · <node name>`. The card foot carries the prototype's *"Ownership
 * decides access"* hint and an `Assign` button with the `user-plus` icon.
 *
 * **Every value is bound to the server's answer.** The prototypes' owner names
 * are mockups and are wrong in both places -- `CU CITI`'s legacy DEM is
 * `Tomasz Różanek`, not `Axel Apice`, and the Business Units screen's CM
 * values are wrong for five of the nine units -- so nothing here is copied but
 * the shape.
 *
 * An owner is an Atlas user ACCOUNT (`user_id`), not a separate roster record:
 * ADR-19's split is superseded, so there is exactly one row per human and this
 * card can never name somebody the Users screen does not know.
 *
 * Ending an owner asks first, naming the holder and the role, because it is a
 * change to who will see what once f9.1 derives scope from these rows. It is
 * not a delete: the row stays, with its `valid_to` set.
 */

import { useState } from 'react';
import { UserMinus, UserPlus } from 'lucide-react';

import type { DeliveryRoleAssignment, LookupValue } from '@/api/client';
import { useEndAssignment, useNodeOwners } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';
import {
  generalMessage,
  initialsOf,
} from '@/routes/business-unit-groups/groupView';

import { AssignOwnerDialog } from './AssignOwnerDialog';

export function OwnershipCard({
  nodeType,
  nodeId,
  nodeName,
  roleCodes,
  deliveryRoles,
  mayManage,
  emptyHint,
}: {
  nodeType: string;
  nodeId: string;
  nodeName: string;
  /** Which delivery roles this level supports. The server is the authority. */
  roleCodes: readonly string[];
  deliveryRoles: readonly LookupValue[];
  /** From the session's capability keys, never from a role code. */
  mayManage: boolean;
  /** What to say when nobody is named. Each level phrases its own gap. */
  emptyHint: string;
}) {
  const owners = useNodeOwners(nodeType, nodeId);
  const [assigning, setAssigning] = useState(false);
  const [ending, setEnding] = useState<DeliveryRoleAssignment | null>(null);

  const current = owners.data?.assignments ?? [];

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h3>Ownership</h3>
        </div>
        <div className="card-body stack-sm">
          {owners.isPending && (
            <span
              className="skeleton-bar group-skeleton-line"
              aria-busy="true"
              aria-label="Loading the owners"
            />
          )}

          {!owners.isPending && owners.error && (
            <p className="hint">The owners could not be loaded.</p>
          )}

          {!owners.isPending && !owners.error && current.length === 0 && (
            <p className="hint">{emptyHint}</p>
          )}

          {current.map((owner) => (
            <div className="row owner-row" key={owner.id}>
              <span className="avatar sm" aria-hidden="true">
                {initialsOf(owner.display_name)}
              </span>
              <div className="owner-identity">
                <div className="user-name">{owner.display_name}</div>
                <div className="hint">
                  {owner.delivery_role.code} &middot; {nodeName}
                </div>
              </div>
              {mayManage && (
                <button
                  type="button"
                  className="btn btn-secondary btn-icon"
                  title="End this assignment"
                  aria-label={`End ${owner.display_name} as ${owner.delivery_role.name}`}
                  onClick={() => setEnding(owner)}
                >
                  <UserMinus aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="card-foot">
          <span className="hint">Ownership decides access</span>
          {mayManage && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setAssigning(true)}
            >
              <UserPlus aria-hidden="true" /> Assign
            </button>
          )}
        </div>
      </div>

      {assigning && (
        <AssignOwnerDialog
          nodeType={nodeType}
          nodeId={nodeId}
          nodeName={nodeName}
          roleCodes={roleCodes}
          deliveryRoles={deliveryRoles}
          onClose={() => setAssigning(false)}
        />
      )}
      {ending && (
        <EndOwnershipDialog
          nodeType={nodeType}
          nodeId={nodeId}
          assignment={ending}
          onClose={() => setEnding(null)}
        />
      )}
    </>
  );
}

/**
 * The end confirmation, naming the holder and the role.
 *
 * The prototype's `modal narrow` with `alert alert-warning`, as f4.1's
 * deactivate confirmation is. It says plainly that nothing is deleted,
 * because that is the property the whole model rests on.
 */
function EndOwnershipDialog({
  nodeType,
  nodeId,
  assignment,
  onClose,
}: {
  nodeType: string;
  nodeId: string;
  assignment: DeliveryRoleAssignment;
  onClose: () => void;
}) {
  const end = useEndAssignment();

  return (
    <Modal
      title="End this assignment"
      subtitle={`${assignment.display_name}, ${assignment.delivery_role.name}`}
      onClose={onClose}
      onSubmit={() =>
        end.mutate(
          { assignmentId: assignment.id, nodeType, nodeId },
          { onSuccess: onClose },
        )
      }
      busy={end.isPending}
      submitLabel="End assignment"
      submitIcon={<UserMinus aria-hidden="true" />}
      submitClassName="btn btn-danger"
      narrow
      layout="plain"
      message={generalMessage(end.error)}
    >
      <div className="alert alert-warning" role="alert">
        <div>
          <div className="a-title">
            {assignment.display_name} will no longer be {assignment.delivery_role.name}{' '}
            here
          </div>
          <div className="a-body">
            Nothing is deleted. The assignment is dated today and stays on the record,
            so who held the role, and when, remains readable.
          </div>
        </div>
      </div>
    </Modal>
  );
}
